import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { cn } from "../../utils/bem";
import { Modal } from "../Modal/Modal";
import "./MediaSplitter.prefix.css";

const block = cn("media-splitter");

const fmt = (t) => {
  if (!Number.isFinite(t)) return "…";
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
};
const fmtBytes = (n) => {
  if (n == null) return "";
  let v = Number(n);
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i < 2 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const WAVEFORM_MAX_BYTES = 60 * 1024 * 1024;

/**
 * Audio / video splitter. Play the recording, drop cut points (Enter or C
 * at the playhead, or click the timeline), drag them, ask for silence
 * suggestions, name segments, then cut. Clips are stored as uploads and,
 * by default, become tasks at once. The original stays until the manager
 * chooses to delete it on the summary screen.
 *
 * ``source``: ``{ fileUploadId }`` (import page) or ``{ taskId }`` (Data
 * Manager). ``onDone({ job, sourceDeleted })`` fires when the panel closes
 * after a finished job.
 */
export const MediaSplitter = ({ projectId, source, mode = "task", onClose, onDone }) => {
  // "import": the recording is an upload that is not a task yet. Clips
  // are produced as uploads only (they join the import list and are
  // imported with everything else) and the original upload is replaced.
  // "task": the recording is an existing task. Clips become tasks and the
  // original task is deleted when Done is pressed with the box ticked.
  const importMode = mode === "import";
  const api = useAPI();
  const toast = useToast();
  const mediaRef = useRef(null);
  const timelineRef = useRef(null);
  const canvasRef = useRef(null);

  const [info, setInfo] = useState(null); // probe result
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [cuts, setCuts] = useState([]); // sorted cut points (seconds), exclusive of 0 and duration
  const [labels, setLabels] = useState({}); // segment index → label
  const [selected, setSelected] = useState(null); // selected cut index
  const [silences, setSilences] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [noiseDb, setNoiseDb] = useState(-35);
  const [minSilence, setMinSilence] = useState(0.6);
  const [minClip, setMinClip] = useState(1);
  const [precise, setPrecise] = useState(false);
  const [addToProject, setAddToProject] = useState(mode !== "import");
  const [peaks, setPeaks] = useState(null);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [deleteSource, setDeleteSource] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [sourceDeleted, setSourceDeleted] = useState(false);
  const segmentStopRef = useRef(null);

  // Keyed on the ids, not the object: callers pass a fresh ``source``
  // object on every render, and re-probing after the original was
  // deleted would 404 over a finished job.
  const fileUploadId = source?.fileUploadId ?? null;
  const taskId = source?.taskId ?? null;
  const params = useMemo(
    () => (fileUploadId ? { file_upload_id: fileUploadId } : { task_id: taskId }),
    [fileUploadId, taskId],
  );

  // ── probe ──────────────────────────────────────────────────────────────
  // Runs once per source. A failed probe must not re-run on the
  // re-render its own error causes.
  const probedKey = useRef(null);
  useEffect(() => {
    if (job) return undefined;
    const key = `${projectId}:${fileUploadId ?? ""}:${taskId ?? ""}`;
    if (probedKey.current === key) return undefined;
    probedKey.current = key;
    let alive = true;
    setInfo(null);
    setError(null);
    api.callApi("mediaSplitProbe", { params: { pk: projectId }, body: params, errorFilter: () => true }).then((res) => {
      if (!alive) return;
      if (!res || res.error) {
        setError(res?.response?.detail ?? res?.error ?? "Could not read this recording.");
        return;
      }
      setInfo(res);
      if (res.duration) setDuration(res.duration);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectId, params]);

  // ── waveform (audio, smaller files) ────────────────────────────────────
  useEffect(() => {
    if (!info || info.kind !== "audio" || !info.url) return;
    let cancelled = false;
    (async () => {
      try {
        const head = await fetch(info.url, { method: "HEAD", credentials: "same-origin" }).catch(() => null);
        const size = Number(head?.headers?.get("content-length") || 0);
        if (size && size > WAVEFORM_MAX_BYTES) return;
        const res = await fetch(info.url, { credentials: "same-origin" });
        const buf = await res.arrayBuffer();
        if (buf.byteLength > WAVEFORM_MAX_BYTES) return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const audio = await ctx.decodeAudioData(buf.slice(0));
        ctx.close?.();
        if (cancelled) return;
        const ch = audio.getChannelData(0);
        const bins = 1200;
        const per = Math.max(1, Math.floor(ch.length / bins));
        const out = new Float32Array(bins);
        for (let i = 0; i < bins; i++) {
          let max = 0;
          const start = i * per;
          const end = Math.min(ch.length, start + per);
          for (let j = start; j < end; j += 4) {
            const v = Math.abs(ch[j]);
            if (v > max) max = v;
          }
          out[i] = max;
        }
        setPeaks(out);
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setDuration((cur) => (Number.isFinite(cur) && cur > 0 ? cur : audio.duration));
        }
      } catch {
        /* no waveform, timeline still works */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const g = canvas.getContext("2d");
    g.scale(dpr, dpr);
    g.clearRect(0, 0, w, h);
    const styles = getComputedStyle(canvas);
    // Silence shading first, so the waveform sits on top of it.
    if (duration) {
      g.fillStyle = styles.getPropertyValue("--splitter-silence") || "rgba(0,0,0,0.06)";
      for (const s of silences) {
        const x1 = (s.start / duration) * w;
        const x2 = (s.end / duration) * w;
        g.fillRect(x1, 0, Math.max(1, x2 - x1), h);
      }
    }
    g.fillStyle = styles.getPropertyValue("--splitter-wave") || "#6b7bd6";
    if (peaks) {
      const n = peaks.length;
      for (let i = 0; i < n; i++) {
        const x = (i / n) * w;
        const amp = Math.max(1, peaks[i] * (h / 2 - 2));
        g.fillRect(x, h / 2 - amp, Math.max(1, w / n), amp * 2);
      }
    } else {
      g.fillRect(0, h / 2 - 1, w, 2);
    }
  }, [peaks, silences, duration]);

  // ── media element events ──────────────────────────────────────────────
  const onTime = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    setTime(el.currentTime);
  }, []);

  // Stop exactly on the cut. ``timeupdate`` only fires a few times a
  // second, so a frame loop watches the playhead while playing and snaps
  // it back to the marker the moment it is reached.
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    const tick = () => {
      const el = mediaRef.current;
      const stop = segmentStopRef.current;
      if (el && stop != null && el.currentTime >= stop - 0.02) {
        el.pause();
        el.currentTime = stop;
        setTime(stop);
        segmentStopRef.current = null;
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  // The server's ffprobe duration is authoritative. The element reports
  // ``Infinity`` for chunked responses and NaN before metadata, and either
  // would turn every "to" and "length" into 0:00.0 once it replaced the
  // real value, so only a finite, positive number is ever accepted, and
  // only when the probe gave none.
  const forcedDuration = useRef(false);
  const onMetadata = useCallback(
    (e) => {
      const el = e.currentTarget;
      const d = el?.duration;
      if (d === Number.POSITIVE_INFINITY && !(Number.isFinite(duration) && duration > 0) && !forcedDuration.current) {
        // Chunked responses come back with an unknown length; seeking far
        // past the end makes the browser work out the real duration and
        // fire durationchange again with a finite value.
        forcedDuration.current = true;
        try {
          el.currentTime = 1e101;
        } catch {
          /* not seekable yet */
        }
        return;
      }
      if (!Number.isFinite(d) || d <= 0) return;
      if (forcedDuration.current && el.currentTime > d - 0.5) {
        el.currentTime = 0;
        forcedDuration.current = false;
      }
      setDuration((cur) => (Number.isFinite(cur) && cur > 0 ? cur : d));
    },
    [duration],
  );

  const seek = useCallback(
    (t) => {
      const el = mediaRef.current;
      if (!el) return;
      const v = clamp(t, 0, duration || el.duration || 0);
      el.currentTime = v;
      setTime(v);
    },
    [duration],
  );

  // Play runs from the playhead to the NEXT cut and stops there, so what
  // you hear is the clip you are defining. ``through`` (Shift+Space or the
  // Play through button) ignores the cuts.
  const togglePlay = useCallback(
    (through = false) => {
      const el = mediaRef.current;
      if (!el) return;
      if (!el.paused) {
        el.pause();
        segmentStopRef.current = null;
        return;
      }
      const t = el.currentTime;
      segmentStopRef.current = through ? null : (cuts.find((c) => c > t + 0.05) ?? null);
      el.play().catch(() => {});
    },
    [cuts],
  );

  // ── cuts ───────────────────────────────────────────────────────────────
  const addCut = useCallback(
    (at) => {
      const t = at ?? mediaRef.current?.currentTime ?? 0;
      if (!duration || t <= 0.05 || t >= duration - 0.05) return;
      setCuts((cs) => {
        if (cs.some((c) => Math.abs(c - t) < 0.05)) return cs;
        return [...cs, Number(t.toFixed(3))].sort((a, b) => a - b);
      });
    },
    [duration],
  );
  const removeCut = useCallback((idx) => {
    setCuts((cs) => cs.filter((_, i) => i !== idx));
    setSelected(null);
  }, []);
  const moveCut = useCallback(
    (idx, t) => {
      setCuts((cs) => {
        const next = [...cs];
        const lo = (idx > 0 ? next[idx - 1] : 0) + 0.05;
        const hi = (idx < next.length - 1 ? next[idx + 1] : duration) - 0.05;
        next[idx] = Number(clamp(t, lo, hi).toFixed(3));
        return next;
      });
    },
    [duration],
  );

  const segments = useMemo(() => {
    const bounds = [0, ...cuts, duration];
    const out = [];
    for (let i = 0; i < bounds.length - 1; i++) {
      out.push({ index: i, start: bounds[i], end: bounds[i + 1] });
    }
    return out;
  }, [cuts, duration]);
  const tooShort = segments.filter((s) => s.end - s.start < minClip);

  // ── keyboard ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (job) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay(e.shiftKey);
      } else if (e.key === "Enter" || e.key.toLowerCase() === "c") {
        e.preventDefault();
        addCut();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        seek((mediaRef.current?.currentTime ?? 0) - (e.shiftKey ? 5 : 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        seek((mediaRef.current?.currentTime ?? 0) + (e.shiftKey ? 5 : 1));
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected != null) {
        e.preventDefault();
        removeCut(selected);
      } else if (e.key === "[" && selected != null) {
        moveCut(selected, cuts[selected] - 0.1);
      } else if (e.key === "]" && selected != null) {
        moveCut(selected, cuts[selected] + 0.1);
      } else if (e.key === "Home") {
        seek(0);
      } else if (e.key === "End") {
        seek(duration);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [job, togglePlay, addCut, seek, selected, removeCut, moveCut, cuts, duration]);

  // ── timeline pointer handling ──────────────────────────────────────────
  const timeAtEvent = (e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    return clamp(((e.clientX - rect.left) / rect.width) * duration, 0, duration);
  };
  const dragging = useRef(null);
  const onTimelinePointerDown = (e) => {
    if (!duration) return;
    const t = timeAtEvent(e);
    // Grab a marker when close to one (within 6 px).
    const rect = timelineRef.current.getBoundingClientRect();
    const px = rect.width / duration;
    const near = cuts.findIndex((c) => Math.abs(c - t) * px < 6);
    if (near >= 0) {
      dragging.current = near;
      setSelected(near);
      timelineRef.current.setPointerCapture(e.pointerId);
      return;
    }
    setSelected(null);
    seek(t);
  };
  const onTimelinePointerMove = (e) => {
    if (dragging.current == null) return;
    moveCut(dragging.current, timeAtEvent(e));
  };
  const onTimelinePointerUp = (e) => {
    if (dragging.current == null) return;
    dragging.current = null;
    try {
      timelineRef.current.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  // ── silence suggestions ────────────────────────────────────────────────
  const suggest = async () => {
    setSuggesting(true);
    const res = await api.callApi("mediaSplitSilences", {
      params: { pk: projectId },
      body: { ...params, noise_db: noiseDb, min_silence: minSilence },
      errorFilter: () => true,
    });
    setSuggesting(false);
    if (!res || res.error) {
      toast.show({ message: res?.response?.detail ?? res?.error ?? "Could not analyse the recording.", type: "error" });
      return;
    }
    const spans = res.silences ?? [];
    setSilences(spans);
    // One cut in the middle of each silence, then drop cuts that would
    // leave a segment shorter than the minimum.
    const mids = spans.map((s) => (s.start + s.end) / 2).filter((t) => t > 0.05 && t < duration - 0.05);
    const kept = [];
    let last = 0;
    for (const t of mids) {
      if (t - last >= minClip) {
        kept.push(Number(t.toFixed(3)));
        last = t;
      }
    }
    if (kept.length && duration - last < minClip) kept.pop();
    setCuts(kept);
    setSelected(null);
    toast.show({
      message: kept.length
        ? `${spans.length} quiet ${spans.length === 1 ? "span" : "spans"} found, ${kept.length + 1} clips suggested. Adjust the markers, then cut.`
        : "No quiet spans found at this setting. Lower the threshold or the minimum silence and try again.",
    });
  };

  // ── run the job ────────────────────────────────────────────────────────
  const playSegment = (seg) => {
    const el = mediaRef.current;
    if (!el) return;
    segmentStopRef.current = seg.end;
    el.currentTime = seg.start;
    el.play().catch(() => {});
  };

  const start = async () => {
    if (!segments.length || running) return;
    setRunning(true);
    const body = {
      source: { ...params, duration },
      cuts: segments.map((s) => ({ start: s.start, end: s.end, label: labels[s.index] || "" })),
      options: { precise, add_to_project: importMode ? false : addToProject },
    };
    const created = await api.callApi("mediaSplitCreate", { params: { pk: projectId }, body, errorFilter: () => true });
    if (!created || created.error) {
      setRunning(false);
      toast.show({
        message: created?.response?.detail ?? created?.error ?? "Could not start the split.",
        type: "error",
      });
      return;
    }
    mediaRef.current?.pause?.();
    setJob(created);
    let state = created;
    while (state && state.status !== "done" && state.status !== "failed") {
      state = await api.callApi("mediaSplitRun", {
        params: { pk: projectId, job: created.id },
        errorFilter: () => true,
      });
      if (!state || state.error) {
        toast.show({
          message: state?.response?.detail ?? state?.error ?? "The split stopped; try again.",
          type: "error",
        });
        break;
      }
      setJob(state);
    }
    setRunning(false);
    if (importMode && state && state.status === "done") {
      // The clips replace the upload; nothing to decide.
      await removeSource(state);
    }
  };

  const removeSource = async (current = job) => {
    if (!current || deleting) return false;
    setDeleting(true);
    const res = await api.callApi("mediaSplitDeleteSource", {
      params: { pk: projectId, job: current.id },
      errorFilter: () => true,
    });
    setDeleting(false);
    if (!res || res.error) {
      toast.show({ message: res?.response?.detail ?? res?.error ?? "Could not delete the original.", type: "error" });
      return false;
    }
    setSourceDeleted(true);
    setJob(res.job);
    return true;
  };

  const finish = async () => {
    let deleted = sourceDeleted;
    if (!importMode && job?.status === "done" && deleteSource && !sourceDeleted) {
      deleted = await removeSource();
      if (!deleted) return; // stay open so the error is visible
    }
    onDone?.({ job, sourceDeleted: deleted });
    onClose?.();
  };

  const isVideo = info?.kind === "video";
  const title = info ? `Split ${info.name}` : "Split recording";

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <Modal
      visible
      onHide={job && running ? undefined : onClose}
      title={title}
      style={{ width: 960, maxWidth: "calc(100vw - 24px)" }}
    >
      <div className={block.toClassName()}>
        {error && <p className={block.elem("error").toClassName()}>{error}</p>}
        {!error && !info && <p className={block.elem("hint").toClassName()}>Reading the recording…</p>}
        {info && !info.can_split && (
          <p className={block.elem("error").toClassName()}>
            Splitting video needs ffmpeg on the server, which is not installed here.
          </p>
        )}

        {info && !job && (
          <>
            <div className={block.elem("player").mod({ video: isVideo }).toClassName()}>
              {isVideo ? (
                // biome-ignore lint/a11y/useMediaCaption: the recording being cut is raw source material without captions
                <video
                  ref={mediaRef}
                  src={info.url}
                  preload="metadata"
                  playsInline
                  onClick={() => togglePlay(false)}
                  onTimeUpdate={onTime}
                  onLoadedMetadata={onMetadata}
                  onDurationChange={onMetadata}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                />
              ) : (
                // biome-ignore lint/a11y/useMediaCaption: raw source recording, no caption track exists yet
                <audio
                  ref={mediaRef}
                  src={info.url}
                  preload="metadata"
                  onTimeUpdate={onTime}
                  onLoadedMetadata={onMetadata}
                  onDurationChange={onMetadata}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                />
              )}
            </div>

            <div className={block.elem("transport").toClassName()}>
              <Button
                type="button"
                size="small"
                look="outlined"
                onClick={() => togglePlay(false)}
                aria-label={playing ? "Pause" : "Play to the next cut"}
                tooltip={playing ? undefined : "Plays from the playhead to the next cut"}
              >
                {playing ? "Pause" : "Play"}
              </Button>
              {!playing && cuts.length > 0 && (
                <Button
                  type="button"
                  size="small"
                  look="string"
                  onClick={() => togglePlay(true)}
                  aria-label="Play through all cuts"
                >
                  Play through
                </Button>
              )}
              <Button type="button" size="small" variant="primary" onClick={() => addCut()} disabled={!duration}>
                Cut here
              </Button>
              <span className={block.elem("clock").toClassName()}>
                {fmt(time)} / {fmt(duration)}
              </span>
              <span className={block.elem("keys").toClassName()}>
                Space plays to the next cut (Shift+Space plays through), Enter or C cuts, arrows seek (Shift 5 s),
                Delete removes the selected marker, [ ] nudge it
              </span>
            </div>

            <div
              ref={timelineRef}
              className={block.elem("timeline").toClassName()}
              onPointerDown={onTimelinePointerDown}
              onPointerMove={onTimelinePointerMove}
              onPointerUp={onTimelinePointerUp}
              role="slider"
              aria-label="Timeline"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration)}
              aria-valuenow={Math.round(time)}
              tabIndex={0}
            >
              <canvas ref={canvasRef} className={block.elem("wave").toClassName()} />
              {duration > 0 &&
                segments.map((s, i) => (
                  <span
                    key={`seg-${i}`}
                    className={block
                      .elem("segment")
                      .mod({ odd: i % 2 === 1, short: s.end - s.start < minClip })
                      .toClassName()}
                    style={{
                      left: `${(s.start / duration) * 100}%`,
                      width: `${((s.end - s.start) / duration) * 100}%`,
                    }}
                  >
                    <b>{i + 1}</b>
                  </span>
                ))}
              {duration > 0 &&
                cuts.map((c, i) => (
                  <span
                    key={`cut-${i}`}
                    className={block
                      .elem("cut")
                      .mod({ selected: selected === i })
                      .toClassName()}
                    style={{ left: `${(c / duration) * 100}%` }}
                    title={fmt(c)}
                  />
                ))}
              {duration > 0 && (
                <span
                  className={block.elem("playhead").toClassName()}
                  style={{ left: `${(time / duration) * 100}%` }}
                />
              )}
            </div>

            <div className={block.elem("tools").toClassName()}>
              <div className={block.elem("tool").toClassName()}>
                <span>Suggest cuts from silence</span>
                <label>
                  Quieter than
                  <input
                    type="number"
                    value={noiseDb}
                    min={-90}
                    max={-5}
                    step={1}
                    onChange={(e) => setNoiseDb(Number(e.target.value))}
                  />{" "}
                  dB
                </label>
                <label>
                  for at least
                  <input
                    type="number"
                    value={minSilence}
                    min={0.1}
                    max={30}
                    step={0.1}
                    onChange={(e) => setMinSilence(Number(e.target.value))}
                  />{" "}
                  s
                </label>
                <Button
                  type="button"
                  size="small"
                  look="outlined"
                  onClick={suggest}
                  disabled={suggesting || !duration}
                  waiting={suggesting}
                >
                  {suggesting ? "Listening…" : "Suggest"}
                </Button>
              </div>
              <div className={block.elem("tool").toClassName()}>
                <label>
                  Shortest clip
                  <input
                    type="number"
                    value={minClip}
                    min={0.2}
                    max={3600}
                    step={0.5}
                    onChange={(e) => setMinClip(Number(e.target.value) || 0.2)}
                  />{" "}
                  s
                </label>
                {isVideo && (
                  <label className={block.elem("check").toClassName()}>
                    <input type="checkbox" checked={precise} onChange={(e) => setPrecise(e.target.checked)} />
                    Frame-exact cuts (re-encodes, slower)
                  </label>
                )}
                {importMode ? (
                  <span className={block.elem("note").toClassName()}>
                    The clips replace this upload in your list and are imported with the rest.
                  </span>
                ) : (
                  <label className={block.elem("check").toClassName()}>
                    <input type="checkbox" checked={addToProject} onChange={(e) => setAddToProject(e.target.checked)} />
                    Add clips to the project as tasks
                  </label>
                )}
                <Button
                  type="button"
                  size="smaller"
                  look="string"
                  onClick={() => {
                    setCuts([]);
                    setSilences([]);
                    setSelected(null);
                  }}
                  disabled={!cuts.length}
                >
                  Clear cuts
                </Button>
              </div>
            </div>

            <div className={block.elem("segments").toClassName()}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Length</th>
                    <th>Label</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {segments.map((s, i) => (
                    <tr
                      key={i}
                      className={block
                        .elem("row")
                        .mod({ short: s.end - s.start < minClip })
                        .toClassName()}
                    >
                      <td>{i + 1}</td>
                      <td>{fmt(s.start)}</td>
                      <td>{fmt(s.end)}</td>
                      <td>{fmt(s.end - s.start)}</td>
                      <td>
                        <input
                          className={block.elem("label-input").toClassName()}
                          value={labels[i] ?? ""}
                          placeholder="e.g. speaker A, intro"
                          maxLength={200}
                          onChange={(e) => setLabels((l) => ({ ...l, [i]: e.target.value }))}
                        />
                      </td>
                      <td className={block.elem("row-actions").toClassName()}>
                        <button type="button" onClick={() => playSegment(s)} title="Play this clip">
                          Play
                        </button>
                        {i < segments.length - 1 && (
                          <button type="button" onClick={() => removeCut(i)} title="Merge with the next clip">
                            Merge
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={block.elem("footer").toClassName()}>
              <span className={block.elem("summary").toClassName()}>
                {segments.length} {segments.length === 1 ? "clip" : "clips"}
                {tooShort.length > 0 && ` (${tooShort.length} shorter than ${minClip} s)`}
                {info.duration ? `, ${fmt(info.duration)} in total` : ""}
              </span>
              <div className={block.elem("actions").toClassName()}>
                <Button type="button" look="outlined" size="medium" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="medium"
                  onClick={start}
                  disabled={
                    running ||
                    !info.can_split ||
                    !duration ||
                    segments.length < 1 ||
                    (segments.length === 1 && cuts.length === 0)
                  }
                  waiting={running}
                >
                  {running ? "Starting…" : `Cut ${segments.length} ${segments.length === 1 ? "clip" : "clips"}`}
                </Button>
              </div>
            </div>
          </>
        )}

        {job && (
          <div className={block.elem("progress").toClassName()}>
            <div
              className={block.elem("bar").toClassName()}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={job.total}
              aria-valuenow={job.progress}
            >
              <span
                style={{
                  width: `${job.status === "done" ? 100 : job.total ? Math.round((job.progress / job.total) * 100) : 0}%`,
                }}
              />
            </div>
            <p className={block.elem("status").toClassName()}>
              {job.status === "failed"
                ? `Stopped after ${job.progress} of ${job.total}: ${job.error}`
                : job.status === "done"
                  ? importMode
                    ? `${job.total} ${job.total === 1 ? "clip is" : "clips are"} in your upload list${sourceDeleted ? "; the original upload was removed" : ""}.`
                    : `${job.total} ${job.total === 1 ? "clip" : "clips"} ready${job.options?.add_to_project ? " and added to the project as tasks" : ""}.`
                  : `Cutting clip ${Math.min(job.total, job.progress + 1)} of ${job.total}…`}
            </p>
            {job.results?.length > 0 && (
              <ul className={block.elem("results").toClassName()}>
                {job.results.map((r) => (
                  <li key={r.index}>
                    <span>{r.index + 1}.</span>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.file}
                    </a>
                    <span>
                      {fmt(r.start)} to {fmt(r.end)}, {fmtBytes(r.size)}
                      {r.label ? `, ${r.label}` : ""}
                      {r.task_id ? `, task #${r.task_id}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {(job.status === "done" || job.status === "failed") && (
              <div className={block.elem("after").toClassName()}>
                {!importMode && job.status === "done" && !sourceDeleted && (
                  <label className={block.elem("check").toClassName()}>
                    <input type="checkbox" checked={deleteSource} onChange={(e) => setDeleteSource(e.target.checked)} />
                    Delete the original recording{job.source?.task_id ? " and its task" : ""} (the clips replace it)
                  </label>
                )}
                {!importMode && sourceDeleted && (
                  <p className={block.elem("hint").toClassName()}>Original recording deleted.</p>
                )}
                <div className={block.elem("actions").toClassName()}>
                  <Button
                    type="button"
                    variant="primary"
                    size="medium"
                    onClick={finish}
                    disabled={deleting}
                    waiting={deleting}
                  >
                    {deleting ? "Finishing…" : "Done"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
